import type { Playbook } from "@workspace/api-client-react";

/**
 * Resolve the best background image for a playbook/media card.
 *
 * Priority:
 *   1. The item's own artwork (`thumbnailUrl`, or a YouTube frame from `videoUrl`).
 *   2. A contextual, on-theme Unsplash photo chosen by category.
 *
 * This lets cards that already have an image use it as their full-bleed
 * background, while imageless cards (articles, plain text triggers) still get
 * an interesting, relevant backdrop instead of a flat colour.
 */

function extractYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1]! : null;
}

const UNSPLASH = (id: string) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=800&q=80`;

/** Contextual fallback photo per library category (lowercased keys). */
const CATEGORY_IMAGE: Record<string, string> = {
  health: UNSPLASH("photo-1544367567-0f2fcb009e0b"), // wellness / movement
  finance: UNSPLASH("photo-1454165804606-c3d57bc86b40"), // charts / money
  "self-care": UNSPLASH("photo-1470071459604-3b5ec3a7fe05"), // calm nature
  "self-growth": UNSPLASH("photo-1488190211105-8b0e65b80b4e"), // sunrise / path
  tech: UNSPLASH("photo-1518770660439-4636190af475"), // circuitry
  other: UNSPLASH("photo-1499750310107-5fef28a66643"), // desk / writing
};

const DEFAULT_IMAGE = CATEGORY_IMAGE.other!;

export function categoryImage(category?: string | null): string {
  if (!category) return DEFAULT_IMAGE;
  return CATEGORY_IMAGE[category.trim().toLowerCase()] ?? DEFAULT_IMAGE;
}

export function getPlaybookImage(
  playbook: Pick<Playbook, "videoUrl" | "category"> & { thumbnailUrl?: string | null },
): string {
  if (playbook.thumbnailUrl) return playbook.thumbnailUrl;
  const youtubeId = playbook.videoUrl ? extractYouTubeId(playbook.videoUrl) : null;
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  return categoryImage(playbook.category);
}
